import * as Sentry from '@sentry/react';
import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { isLoggedIn, saveTokens, clearTokens, getRefreshToken } from './auth.js';
import apiClient from './api.js';
import {
  logout as apiLogout,
  getMe,
  refreshTokens,
} from './api.js';

const AuthContext = createContext(null);

const REFRESH_INTERVAL_MS = 55 * 60 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef(null);

  function startRefreshInterval() {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(async () => {
      try {
        const { data } = await refreshTokens();
        saveTokens(data.access_token, data.refresh_token);
      } catch {
        clearTokens();
        setUser(null);
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }, REFRESH_INTERVAL_MS);
  }

  useEffect(() => {
    async function initAuth() {
      if (isLoggedIn()) {
        try {
          const { data } = await getMe();
          setUser(data);
          Sentry.setUser({ id: data.id, email: data.email });
          startRefreshInterval();
        } catch {
          clearTokens();
        }
      }
      setLoading(false);
    }
    initAuth();

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  async function login(code, { ref, invite } = {}) {
    let url = `/auth/google/callback?code=${encodeURIComponent(code)}`
    if (ref) url += `&ref=${encodeURIComponent(ref)}`
    if (invite) url += `&invite=${encodeURIComponent(invite)}`
    const { data } = await apiClient.post(url);
    saveTokens(data.access_token, data.refresh_token);
    const { data: userData } = await getMe();
    setUser(userData);
    Sentry.setUser({ id: userData.id, email: userData.email });
    startRefreshInterval();
  }

  async function logout() {
    try {
      await apiLogout(getRefreshToken());
    } catch {
      // Proceed with local logout even if server call fails
    }
    clearTokens();
    Sentry.setUser(null);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setUser(null);
  }

  async function refreshUser() {
    try {
      const { data } = await getMe();
      setUser(data);
    } catch {
      // Ignore — user state stays as-is if the refresh fails
    }
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
